import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Who is signed in.
 *
 * The session lives in an HttpOnly cookie, so this never sees or stores a token
 * — it asks the server who it is talking to and believes the answer. That also
 * means signing out is a request, not a state change: clearing this alone would
 * leave a cookie behind that still opens every API route.
 */
const AuthContext = createContext({
  user: null,
  ready: false,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  chooseProfile: async () => {},
  leaveProfile: async () => {},
  opening: false,
  endOpening: () => {}
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Nothing renders until this is true. Deciding what to show before the
  // session is known is what makes the app flash the login screen at someone
  // who was already signed in.
  const [ready, setReady] = useState(false);
  // The full-screen opening plays after signing in, and only then
  const [opening, setOpening] = useState(false);
  const endOpening = useCallback(() => setOpening(false), []);

  useEffect(() => {
    let cancelled = false;

    axios
      .get('/api/auth/me')
      .then(({ data }) => {
        if (!cancelled) setUser(data.data || null);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (username, password) => {
    const { data } = await axios.post('/api/auth/login', { username, password });
    // An account with one profile comes back already watching as them; an
    // account with several comes back with `profile: null`, which is what the
    // router reads as "show the picker".
    setUser(data.data);
    setOpening(true);
    return data.data;
  }, []);

  /**
   * Make an account with an invite code. The server signs the new account in
   * on the same response, so from here on it is exactly a sign-in.
   */
  const signUp = useCallback(async (code, username, password) => {
    const { data } = await axios.post('/api/invite/signup', { code, username, password });
    setUser(data.data);
    setOpening(true);
    return data.data;
  }, []);

  /**
   * Watch as one of the account's profiles.
   *
   * A full reload rather than a state change, for the same reason signing out is
   * one: the library, catalog and my-list providers below this have all loaded
   * (or been refused) for whoever was here before, and a profile switch changes
   * every answer they hold. Reloading is a beat slower and cannot leave one
   * person's continue-watching rail on screen under another person's name.
   */
  const chooseProfile = useCallback(async (id) => {
    await axios.post('/api/auth/profile', { id });
    window.location.assign('/home');
  }, []);

  /** Back to the picker without signing out. */
  const leaveProfile = useCallback(async () => {
    try {
      await axios.post('/api/auth/profile/leave');
    } finally {
      window.location.assign('/home');
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } finally {
      setUser(null);
      // A full reload rather than a route change: every provider below this one
      // is holding data belonging to the person signing out.
      window.location.assign('/login');
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, ready, signIn, signUp, signOut, chooseProfile, leaveProfile, opening, endOpening }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
